import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  incrementQuantity,
  decrementQuantity,
  removeFromCart,
} from './CartReducer';
import Headercomponent from './Headercomponent';

const CartScreen = () => {
  const cartItems = useSelector((state) => state.cart.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Calculate subtotal for a single item
  const calculateSubtotal = (item) => parseFloat(item.Price) * item.quantity;

  // Calculate total for all items in the cart
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + calculateSubtotal(item), 0);
  };

  // Handlers for increment, decrement, and remove
  const handleIncrement = (item) => dispatch(incrementQuantity(item));
  const handleDecrement = (item) => dispatch(decrementQuantity(item));
  const handleRemove = (item) => dispatch(removeFromCart(item));

  // Handler for checkout
  const handleCheckout = () => {
    const total = calculateTotal();
    navigate('/Checkout', { state: { total } });
  };

  // Load cart from local storage on component mount
  useEffect(() => {
    const cartData = localStorage.getItem('cart');
    if (cartData) {
      dispatch({ type: 'cart/loadFromStorage', payload: JSON.parse(cartData) });
    }
  }, [dispatch]);

  // Update local storage whenever cartItems change
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  return (
    <div style={styles.cartContainer}>
      <Headercomponent title="Your Shopping Cart" itemCount={cartItems.length} />

      {cartItems.length === 0 ? (
        <div style={styles.emptyCartContainer}>
          <img src="/empty-cart.svg" alt="Empty cart" style={styles.emptyCartImage} />
          <p style={styles.emptyCartText}>Your cart is currently empty.</p>
        </div>
      ) : (
        <div style={styles.cartItems}>
          {cartItems.map((item) => (
            <div key={item.ID} style={styles.cartItem}>
              <img src={item.image} alt={item.ID} style={styles.cartItemImage} />
              <div style={styles.cartItemDetails}>
                <h2 style={styles.cartItemName}>{item.ID}</h2>
                <p style={styles.cartItemPrice}>
                  ₦{parseFloat(item.Price).toLocaleString()}
                </p>
                <div style={styles.cartItemQuantityControls}>
                  <button
                    onClick={() => handleDecrement(item)}
                    style={styles.quantityButton}
                    aria-label="Decrease quantity"
                  >
                    -
                  </button>
                  <span style={styles.cartItemQuantity}>{item.quantity}</span>
                  <button
                    onClick={() => handleIncrement(item)}
                    style={styles.quantityButton}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                <p style={styles.cartItemSubtotal}>
                  Subtotal: ₦{calculateSubtotal(item).toLocaleString()}
                </p>
                <button
                  onClick={() => handleRemove(item)}
                  style={styles.removeButton}
                  aria-label="Remove item"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {cartItems.length > 0 && (
        <div style={styles.fixedFooter}>
          <div style={styles.transparentTotalBox}>
            <p style={styles.cartTotal}>
              Total: ₦{calculateTotal().toLocaleString()}
            </p>
          </div>
          <button onClick={handleCheckout} style={styles.checkoutButton}>
            Proceed to Checkout
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  cartContainer: {
    padding: '20px',
    backgroundColor: '#fafafa',
    maxWidth: '900px',
    margin: '0 auto',
    borderRadius: '12px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease-in-out',
    marginBottom: '120px', // Adjusted for better spacing with fixed footer
  },
  emptyCartContainer: {
    textAlign: 'center',
    marginTop: '50px',
  },
  emptyCartImage: {
    width: '180px',
    marginBottom: '20px',
  },
  emptyCartText: {
    fontSize: '1.3rem',
    color: '#aaa',
  },
  cartItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  cartItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    borderBottom: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
    gap: '15px',
  },
  cartItemImage: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  cartItemDetails: {
    flex: 1,
  },
  cartItemName: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#007B33',
    marginBottom: '8px',
    wordBreak: 'break-word', // Ensures long words break and wrap
    whiteSpace: 'normal', // Allows text to wrap to the next line
  },
  cartItemPrice: {
    fontSize: '0.9rem',
    color: '#444',
    margin: '5px 0',
  },
  cartItemQuantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: '12px 0',
  },
  quantityButton: {
    fontSize: '1rem', // Adjusted font size for consistency
    padding: '8px 12px', // Adjusted padding for consistency
    backgroundColor: '#007B33',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    width: '40px', // Fixed width for both buttons
    textAlign: 'center', // Center the text inside the button
    ':hover': {
      backgroundColor: '#005a28',
    },
  },
  cartItemQuantity: {
    fontSize: '1rem',
    color: '#333',
  },
  cartItemSubtotal: {
    fontSize: '1rem',
    color: '#333',
  },
  removeButton: {
    fontSize: '0.9rem',
    color: '#e74c3c',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    ':hover': {
      color: '#c0392b',
    },
  },
  fixedFooter: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    padding: '15px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderTop: '1px solid #ddd',
    boxShadow: '0 -4px 8px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  transparentTotalBox: {
    width: '100%',
    textAlign: 'center',
  },
  cartTotal: {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    color: '#007B33',
  },
  checkoutButton: {
    fontSize: '1rem',
    padding: '12px 20px',
    backgroundColor: '#007B33',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '300px',
    transition: 'background-color 0.2s ease',
    textAlign: 'center',
    ':hover': {
      backgroundColor: '#005a28',
    },
  },
};

export default CartScreen;